import { useMemo } from "react";
import { useFragment } from "react-relay";

import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { graphql } from "relay-runtime";

import type { usePreferredTimezone_viewer$key } from "~/__generated__/usePreferredTimezone_viewer.graphql";

const viewerFragment = graphql`
	fragment usePreferredTimezone_viewer on Viewer {
		settings {
			timezonePreferences {
				preferredTimezone {
					timezone {
						name
					}
				}
			}
		}
	}
`;

/** A hook that providers helpers for adjusting JS Dates into the user's timezone */
export default function usePreferredTimezone(viewerKey: usePreferredTimezone_viewer$key) {
	const viewer = useFragment(viewerFragment, viewerKey);
	const preferredTimezoneName = viewer.settings.timezonePreferences.preferredTimezone.timezone.name;

	const { workingTimezone, systemToUserTime, userToSystemTime } = useMemo(() => {
		const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		const userTimezone = preferredTimezoneName;
		if (userTimezone === "")
			return {
				workingTimezone: systemTimezone,
				systemToUserTime: (date: Date) => date,
				userToSystemTime: (date: Date) => date,
			};

		return {
			workingTimezone: userTimezone,
			systemToUserTime: (date: Date) => {
				const utcTime = fromZonedTime(date, systemTimezone);
				return toZonedTime(utcTime, userTimezone);
			},
			userToSystemTime: (date: Date) => {
				const utcTime = fromZonedTime(date, userTimezone);
				return toZonedTime(utcTime, systemTimezone);
			},
		};
	}, [preferredTimezoneName]);

	return { workingTimezone, systemToUserTime, userToSystemTime };
}
